/**
 * 큰 첨부 파일의 **바이트 공유 캐시** (2026-08-13).
 *
 * ## 왜 필요한가
 *
 * 워크스페이스에서 PDF 를 열면 같은 파일을 **두 번** 받고 있었다:
 *   ① 파일 클릭 → 티어1 미리보기(`PdfPreview`)가 전체 바이트를 fetch
 *   ② "편집" 클릭 → 인앱 편집기(`@workchat/office/pdf`)의 shim 이 **또** fetch
 *
 * 26MB 짜리 국가 가이드라인 PDF 로 재현했을 때 합계 52MB 를 받고, 각 단계는 파일이
 * 100% 도착할 때까지 아무것도 못 그린다. 사용자 회선이 2MB/s 면 그것만 26초다.
 *
 * → fileId 단위로 **한 번만 받아 공유**한다. 미리보기가 이미 받아 둔 바이트를 편집기가
 *   그대로 쓰고, 편집기 → 미리보기 순서여도 마찬가지다.
 *
 * ## 메모리 정책
 *
 * 26MB 짜리를 여러 개 들고 있으면 그게 다시 문제가 된다. **가장 최근 것 하나만** 남긴다
 * (문서 패널은 한 번에 하나를 여는 UI 라 이걸로 충분하다). 저장 등으로 파일이 바뀌면
 * `invalidateFileBytes` 로 명시적으로 버린다.
 */
import { requestPresignedDownload } from '../api/files';

/** 진행률 콜백 — 받은 바이트/전체(모르면 null). */
export type ProgressFn = (received: number, total: number | null) => void;

interface Entry {
  fileId: string;
  promise: Promise<Uint8Array>;
}

/** 가장 최근에 받은 파일 하나만 유지한다(위 "메모리 정책" 참조). */
let current: Entry | null = null;

/**
 * 파일 바이트를 받는다. 같은 fileId 로 이미 받았거나 받는 중이면 **그것을 그대로 준다**.
 *
 * ⚠ 반환한 배열은 **공유본**이다 — 호출자가 내용을 바꾸면 안 되고, 소유권을 넘기는 곳
 *   (pdf.js 워커로 transfer 하는 등)에는 `.slice()` 로 복사해서 넘겨야 한다.
 */
export function fetchFileBytesShared(fileId: string, onProgress?: ProgressFn): Promise<Uint8Array> {
  if (current?.fileId === fileId) return current.promise;
  const entry: Entry = { fileId, promise: download(fileId, onProgress) };
  current = entry;
  entry.promise.catch(() => {
    // 실패한 약속을 남겨 두면 다음 시도까지 같은 실패를 돌려준다.
    if (current === entry) current = null;
  });
  return entry.promise;
}

/**
 * 이미 다른 경로로 받아 둔 바이트를 캐시에 넣는다.
 *
 * 미리보기는 pdf.js 에 **URL 을 주고 Range 로 점진 수신**하므로 우리 손에 바이트가 없다.
 * 다 받은 뒤 `pdf.getData()` 로 꺼내 여기에 넣어 두면, 이어서 "편집" 을 눌렀을 때
 * 편집기가 **네트워크를 한 번도 안 타고** 시작한다.
 */
export function putFileBytes(fileId: string, bytes: Uint8Array): void {
  current = { fileId, promise: Promise.resolve(bytes) };
}

/** 파일 내용이 바뀐 뒤(저장 등) 캐시를 버린다. */
export function invalidateFileBytes(fileId: string): void {
  if (current?.fileId === fileId) current = null;
}

async function download(fileId: string, onProgress?: ProgressFn): Promise<Uint8Array> {
  // 「열람」(§3.4.1) — 이 캐시는 인앱 미리보기 · 편집기가 화면에 그리려고 받는다(기기에 파일로 남지 않는다).
  const { downloadUrl } = await requestPresignedDownload(fileId, { purpose: 'preview' });
  const res = await fetch(downloadUrl);
  if (!res.ok) throw new Error(`다운로드 실패 (${res.status})`);

  const totalHeader = res.headers.get('content-length');
  const total = totalHeader ? Number(totalHeader) : null;

  // 진행률이 필요 없거나 스트림을 못 쓰면 한 번에 받는다(구형 브라우저 안전망).
  if (!onProgress || !res.body) {
    const buf = new Uint8Array(await res.arrayBuffer());
    onProgress?.(buf.byteLength, total);
    return buf;
  }

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    onProgress(received, total && Number.isFinite(total) ? total : null);
  }
  const out = new Uint8Array(received);
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.byteLength; }
  return out;
}

/**
 * 공유 바이트를 **ArrayBuffer 로** 넘겨야 하는 곳에 쓴다.
 *
 * 미리보기 렌더러들(`docx-preview`·`@aiden0z/pptx-renderer`)의 입력 타입이 `any` 라
 * TypeScript 가 잡아주지 않는다. 예전 코드가 ArrayBuffer 를 넘겼으므로 **그대로 유지**해
 * 라이브러리 내부 분기가 달라질 여지를 없앤다.
 *
 * 뷰가 버퍼 전체를 덮으면 복사 없이 그 버퍼를 준다(26~38MB 짜리를 또 복사하지 않기 위해).
 */
export function asArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
    ? (bytes.buffer as ArrayBuffer)
    : (bytes.slice().buffer as ArrayBuffer);
}
